import { Injectable } from '@angular/core';
import { InvoicePdfService } from '../../invoices/services/invoice-pdf.service';
import { FakturaService } from '../../invoices/services/faktura.service';
import { BehaviorSubject, combineLatest, from } from 'rxjs';
import { finalize, first, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { ClientOptionsSelectors } from '../../clients/client-details/store/services';
import { AuthService } from '../../auth/store/services';
import pdfMake from 'pdfmake/build/pdfmake';
import { Settings } from '../../auth/store/models';
import { FinanceChargeTerms } from '../../clients/client-details/store/models';
import { HttpClient } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProgressModalComponent } from '../../shared/progress-modal/progress-modal.component';
import { AlertService } from '../../core/alerts/alert.service';

interface InvoiceToUniconta {
    fakturanr: number;
    glkundenr: number;
    totalbel: number;
    valuta: string;
    dato: string;
    betdato: string;
    _Data: any;
}

@Injectable({
    providedIn: 'root'
})
export class UnicontaService {
    progressMessageSubject = new BehaviorSubject<string>('Overfører til Uniconta');
    progressMessage$ = this.progressMessageSubject.asObservable();

    constructor(
        private invoicePdfService: InvoicePdfService,
        private fakturaService: FakturaService,
        private authService: AuthService,
        private clientOptionsSelector: ClientOptionsSelectors,
        private http: HttpClient,
        private ngbModal: NgbModal,
        private alert: AlertService,
    ) { }

    timeposterToUniconta() {
        const progressModal = this.ngbModal.open(ProgressModalComponent, { size: 'lg', backdrop: 'static', keyboard: false });
        progressModal.componentInstance.title = 'Timeposter til Uniconta';
        progressModal.componentInstance.progressMessage$ = this.progressMessage$;
        combineLatest([
            this.getTimeposterFakturanumreToTransfer(),
            this.authService.getOfficeSettings(),
            this.clientOptionsSelector.financeChargeTerms.pipe(first()),
        ]).pipe(
            switchMap(([fakturanumre, officeSettings, financeChargeTerms]) => from(fakturanumre).pipe(
                mergeMap(fakturanr => from(this.createInvoiceToUniconta(fakturanr, officeSettings, financeChargeTerms))),
            )),
            toArray(),
            switchMap(invoices => this.toUnicontaWithInvoices(invoices.filter(i => i !== undefined))),
            map(() => this.alert.createAlert(`Timeposter overført til Uniconta!`, 'success')),
            finalize(() => progressModal.close()),
        ).subscribe();
    }

    private toUnicontaWithInvoices(invoices: InvoiceToUniconta[]) {
        this.progressMessageSubject.next('Overfører poster og fakturaer til Uniconta');
        const url = `[api]/Timeposter/toUniconta/[officeId]`;
        return this.http.post(url, invoices);
    }

    private getTimeposterFakturanumreToTransfer() {
        const url = `[api]/Timeposter/notTransferredFakturanumreByOffice/[officeId]`;
        return this.http.get<number[]>(url);
    }

    private async getBase64Data(doc: any): Promise<string> {
        const pdfDocGenerator = pdfMake.createPdf(doc);
        return new Promise<string>((resolve) => {
            pdfDocGenerator.getBase64(data => {
                resolve(data);
            });
        });
    }

    private async createInvoiceToUniconta(fakturanr: number, officeSettings: Settings, financeChargeTerms: FinanceChargeTerms[]): Promise<InvoiceToUniconta> {
        const faktura = await this.fakturaService.fakturaByFakturanr(fakturanr).toPromise();
        this.progressMessageSubject.next(`Henter faktura: ${fakturanr}`);
        if (!faktura) { return; }
        const interestRate = financeChargeTerms.find(term => term.code === faktura.kunder.navRentebetingelser)?.interestRate;
        const doc = this.invoicePdfService.getPDFdoc(faktura, officeSettings, interestRate);
        const base64Data = await this.getBase64Data(doc);
        return {
            fakturanr: faktura.fakturanr,
            totalbel: faktura.totalbel,
            valuta: faktura.valuta,
            glkundenr: faktura.kunder.glkundenr,
            dato: faktura.dato,
            betdato: faktura.betdato,
            _Data: base64Data,
        } as InvoiceToUniconta;
    }

    syncUnicontaClients() {
        const progressModal = this.ngbModal.open(ProgressModalComponent, { size: 'lg', backdrop: 'static', keyboard: false });
        progressModal.componentInstance.title = 'Sync kunder til Uniconta';
        progressModal.componentInstance.progressMessage$ = this.progressMessage$;
        const url = `[api]/client/syncUnicontaClients/[officeId]`;
        return this.http.get<number>(url).pipe(
            map((syncedCount) => this.alert.createAlert(`${syncedCount} kunder opdateret i Uniconta!`, 'success')),
            finalize(() => progressModal.close()),
        ).subscribe();
    }
}
